<template>
    <div class="bg-black rounded-lg shadow-md text-white p-6 max-w-4xl mx-auto">
        <div class="flex items-center mb-6">
            <BackButton class="mr-3" />
            <h1 class="text-2xl font-black font-lato">Add New Survey</h1>
        </div>
        
        <div class="space-y-4 mb-6">
            <!-- Survey Date/Time -->
            <div class="flex space-x-4">
                <div class="flex-1">
                    <label class="block text-sm font-medium mb-2">Survey Date & Time</label>
                    <input 
                        type="datetime-local" 
                        v-model="surveyDateTime"
                        class="w-full bg-neutral-700 rounded-lg p-3 text-white border border-neutral-600 focus:border-primary focus:outline-none"
                    >
                </div>
                
                <!-- Survey Type (read-only) -->
                <div class="flex-1">
                    <label class="block text-sm font-medium mb-2">Survey Type</label>
                    <div class="w-full bg-neutral-800 rounded-lg p-3 text-white border border-neutral-700 flex items-center">
                        <span class="text-primary mr-2">📊</span>
                        <span>3D Analysis</span>
                    </div>
                </div>

                <!-- Survey Type (read-only) -->
                <div class="mb-4">
                    <label for="surveyPlot" class="block mb-2">Survey Plot</label>
                    <Dropdown 
                        v-model="selectedSurveyPlot" 
                        :options="surveyPlots"
                        placeholder="Select a Survey Plot"
                        :clearable="true"
                        @clear="clearSurveyPlot"
                    />
                </div>

            </div>
        </div>
        
        <!-- Drag and Drop Area -->
        <div class="mb-6">
            <label class="block text-sm font-medium mb-3">Survey Images</label>
            
            <!-- Hidden file input -->
            <input 
                type="file"
                ref="fileInput"
                class="hidden"
                accept="image/*"
                multiple
                @change="handleFileSelect"
            >
            
            <!-- Main Drag and Drop Zone -->
            <div 
                @click="triggerFileInput"
                @dragover.prevent="handleDragOver"
                @dragleave.prevent="handleDragLeave"
                @drop.prevent="handleDrop"
                :class="[
                    'border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all duration-300',
                    isDragging ? 'border-primary bg-primary bg-opacity-10' : 'border-neutral-600 hover:border-neutral-500'
                ]"
                class="min-h-[300px] flex flex-col items-center justify-center"
            >
                <div class="mb-4">
                    <i class="mdi mdi-cloud-upload text-6xl text-neutral-400"></i>
                </div>
                
                <h3 class="text-xl font-semibold mb-2">
                    {{ isDragging ? 'Drop images here' : 'Drop images or click to browse' }}
                </h3>
                
                <p class="text-neutral-400 mb-4">
                    Upload multiple images for your 3D analysis survey
                </p>
                
                <div class="text-sm text-neutral-500">
                    Supported formats: JPG, PNG, GIF
                </div>
            </div>
            
            <!-- Selected Images Preview -->
            <div v-if="selectedFiles.length > 0" class="mt-4">
                <div class="flex items-center justify-between mb-3">
                    <h4 class="text-lg font-semibold">Selected Images ({{ selectedFiles.length }})</h4>
                    <button 
                        @click="clearAllFiles"
                        class="text-sm text-red-400 hover:text-red-300 transition-colors"
                    >
                        Clear All
                    </button>
                </div>
                
                <!-- Pagination Controls -->
                <div v-if="totalPages > 1" class="flex items-center justify-between mb-4">
                    <div class="text-sm text-neutral-400">
                        Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ selectedFiles.length }} images
                    </div>
                    <div class="flex items-center space-x-2">
                        <button 
                            @click="currentPage--"
                            :disabled="currentPage === 1"
                            class="px-3 py-1 bg-neutral-700 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-600 transition-colors"
                        >
                            Previous
                        </button>
                        <span class="text-sm text-neutral-400">
                            Page {{ currentPage }} of {{ totalPages }}
                        </span>
                        <button 
                            @click="currentPage++"
                            :disabled="currentPage === totalPages"
                            class="px-3 py-1 bg-neutral-700 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-600 transition-colors"
                        >
                            Next
                        </button>
                    </div>
                </div>
                
                <!-- Thumbnail Grid (only current page) -->
                <div class="grid grid-cols-6 md:grid-cols-8 lg:grid-cols-12 gap-2">
                    <div 
                        v-for="(file, index) in currentPageFiles" 
                        :key="startIndex + index"
                        class="relative group bg-neutral-800 rounded overflow-hidden aspect-square"
                    >
                        <!-- Thumbnail Image -->
                        <div 
                            v-if="file.preview"
                            class="w-full h-full"
                        >
                            <img 
                                :src="file.preview" 
                                :alt="file.name"
                                class="w-full h-full object-cover"
                                @error="handleImageError(file)"
                            >
                        </div>
                        
                        <!-- Loading placeholder -->
                        <div 
                            v-else-if="file.loading"
                            class="w-full h-full flex items-center justify-center bg-neutral-700"
                        >
                            <i class="mdi mdi-loading mdi-spin text-neutral-400"></i>
                        </div>
                        
                        <!-- File icon placeholder -->
                        <div 
                            v-else
                            @click="generatePreview(file)"
                            class="w-full h-full flex flex-col items-center justify-center bg-neutral-700 cursor-pointer hover:bg-neutral-600 transition-colors"
                        >
                            <i class="mdi mdi-file-image text-lg text-neutral-400"></i>
                            <span class="text-xs text-neutral-500 mt-1">Click to preview</span>
                        </div>
                        
                        <!-- Remove button overlay -->
                        <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                            <button 
                                @click="removeFile(startIndex + index)"
                                class="opacity-0 group-hover:opacity-100 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-all duration-200"
                                :title="file.name"
                            >
                                <i class="mdi mdi-close text-sm"></i>
                            </button>
                        </div>
                    </div>
                </div>
                
                <!-- Bulk Actions -->
                <div class="mt-4 flex items-center justify-between">
                    <div class="text-sm text-neutral-400">
                        Total size: {{ formatFileSize(totalSize) }}
                    </div>
                    <button 
                        @click="generateAllPreviews"
                        :disabled="allPreviewsGenerated"
                        class="text-sm bg-neutral-700 hover:bg-neutral-600 px-3 py-1 rounded transition-colors disabled:opacity-50"
                    >
                        {{ allPreviewsGenerated ? 'All previews loaded' : 'Load all previews' }}
                    </button>
                </div>
            </div>
        </div>
        
        <!-- Action Buttons -->
        <div class="flex justify-end space-x-3">
            <button 
                @click="cancel"
                class="px-6 py-2 bg-neutral-600 text-white rounded-lg hover:bg-neutral-500 transition-colors"
            >
                Cancel
            </button>
            <button 
                @click="saveSurvey"
                :disabled="!isFormValid || isUploading"
                class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
                <i v-if="isUploading" class="mdi mdi-loading mdi-spin mr-2"></i>
                <span v-if="isUploading">
                    Uploading... {{ uploadedCount }}/{{ totalUploadCount }}
                </span>
                <span v-else>
                    Create Survey
                </span>
            </button>
        </div>
    </div>
</template>

<script>
import BackButton from './utils/BackButton.vue';
import { db_resto_new } from '../main';
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { collection, query, where, getDocs, addDoc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { EventBus } from '@/event-bus.js';
import Dropdown from '@/components/utils/Dropdown.vue';

export default {
    name: 'AddSurvey',
    components: {
        BackButton,
        Dropdown
    },
    props: {
        branchId: {
            type: String,
            required: true
        },
        outplantSiteId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            surveyDateTime: '',
            selectedFiles: [],
            isDragging: false,
            currentPage: 1,
            itemsPerPage: 24, // 6x4 grid on desktop
            isUploading: false,
            uploadProgress: 0,
            uploadedCount: 0,
            totalUploadCount: 0,
            surveyPlots: [],
            selectedSurveyPlot: null
        };
    },
    computed: {
        isFormValid() {
            return this.surveyDateTime && this.selectedFiles.length > 0;
        },
        totalPages() {
            return Math.ceil(this.selectedFiles.length / this.itemsPerPage);
        },
        startIndex() {
            return (this.currentPage - 1) * this.itemsPerPage;
        },
        endIndex() {
            return Math.min(this.startIndex + this.itemsPerPage, this.selectedFiles.length);
        },
        currentPageFiles() {
            return this.selectedFiles.slice(this.startIndex, this.endIndex);
        },
        totalSize() {
            return this.selectedFiles.reduce((sum, file) => sum + file.size, 0);
        },
        allPreviewsGenerated() {
            return this.selectedFiles.every(file => file.preview || file.error);
        }
    },
    mounted() {
        // Pre-fill with current date and time
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        
        this.surveyDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
        this.fetchSurveyPlots();
    },
    methods: {
        triggerFileInput() {
            this.$refs.fileInput.click();
        },
        
        handleFileSelect(event) {
            const files = Array.from(event.target.files);
            this.processFiles(files);
        },
        
        handleDragOver(event) {
            this.isDragging = true;
        },
        
        handleDragLeave(event) {
            this.isDragging = false;
        },
        
        handleDrop(event) {
            this.isDragging = false;
            const files = Array.from(event.dataTransfer.files);
            this.processFiles(files);
        },
        
        processFiles(files) {
            const imageFiles = files.filter(file => file.type.startsWith('image/'));
            
            imageFiles.forEach(file => {
                const fileObj = {
                    file: file,
                    name: file.name,
                    size: file.size,
                    preview: null,
                    loading: false,
                    error: false
                };
                
                this.selectedFiles.push(fileObj);
            });
            
            // Auto-generate previews for first page
            this.$nextTick(() => {
                this.generatePreviewsForCurrentPage();
            });
            
            // Reset file input
            this.$refs.fileInput.value = '';
        },
        
        generatePreviewsForCurrentPage() {
            this.currentPageFiles.forEach(file => {
                if (!file.preview && !file.loading) {
                    this.generatePreview(file);
                }
            });
        },
        
        generatePreview(file) {
            if (file.preview || file.loading) return;
            
            file.loading = true;
            
            const reader = new FileReader();
            reader.onload = (e) => {
                file.preview = e.target.result;
                file.loading = false;
            };
            reader.onerror = () => {
                file.error = true;
                file.loading = false;
            };
            reader.readAsDataURL(file.file);
        },
        
        generateAllPreviews() {
            this.selectedFiles.forEach(file => {
                if (!file.preview && !file.loading) {
                    // Add small delay to prevent browser freeze
                    setTimeout(() => this.generatePreview(file), Math.random() * 100);
                }
            });
        },
        
        handleImageError(file) {
            file.error = true;
        },
        
        removeFile(index) {
            this.selectedFiles.splice(index, 1);
            
            // Adjust current page if needed
            if (this.selectedFiles.length > 0 && this.currentPage > this.totalPages) {
                this.currentPage = this.totalPages;
            }
        },
        
        clearAllFiles() {
            if (confirm(`Are you sure you want to remove all ${this.selectedFiles.length} images?`)) {
                this.selectedFiles = [];
                this.currentPage = 1;
            }
        },
        
        formatFileSize(bytes) {
            if (bytes === 0) return '0 B';
            const k = 1024;
            const sizes = ['B', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
        },

        async fetchSurveyPlots() {
            try {
                const surveyPlotsRef = query(collection(db_resto_new, '_sites'),
                    where('location.outplantID', '==', this.outplantSiteId),
                    where('siteType', '==', 'surveyPlot'))
                const querySnapshot = await getDocs(surveyPlotsRef);
                this.surveyPlots = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    name: doc.data().name,
                    geolocation: doc.data().geolocation
                }));
            } catch (error) {
                console.error('Error fetching survey plots:', error);
                this.surveyPlots = [];
            }

        },

        clearSurveyPlot() {
            this.selectedSurveyPlot = null;
        },
        
        saveSurvey() {
            if (!this.isFormValid || this.isUploading) return;
            
            // Start upload process
            this.uploadSurvey();
        },
        
        async uploadSurvey() {
            this.isUploading = true;
            this.uploadProgress = 0;
            this.uploadedCount = 0;
            this.totalUploadCount = this.selectedFiles.length;
            
            try {
                // Show initial upload toast
                EventBus.$emit('show-toast', {
                    message: `Starting upload of ${this.totalUploadCount} images...`,
                    type: 'primary',
                    loading: true
                });
                
                // Create date folder name (YYYY-MM-DD-HH-MM format)
                const surveyDate = new Date(this.surveyDateTime);
                const year = surveyDate.getFullYear();
                const month = String(surveyDate.getMonth() + 1).padStart(2, '0');
                const day = String(surveyDate.getDate()).padStart(2, '0');
                const hours = String(surveyDate.getHours()).padStart(2, '0');
                const minutes = String(surveyDate.getMinutes()).padStart(2, '0');
                const dateFolder = `${year}-${month}-${day}-${hours}-${minutes}`;
                
                // Create survey document first to get the ID
                const surveyData = {
                    type: '3D Analysis',
                    date: surveyDate,
                    status: 'uploading',
                    imageUrls: [], // Will be updated after upload
                    imageCount: this.totalUploadCount,
                    uploadDate: new Date(),
                };

                if (!user) {
                    console.error('No authenticated user found');
                    return;
                }
                
                const surveyEvent = {
                    metadata: {},
                    location: {
                        branchID: this.branchId,
                        outplantID: this.outplantSiteId,
                        surveyID: null,         // needs to be filled in from surveySite
                    },
                    eventType: 'coralSurvey',
                    eventData: surveyData
                };
                
                // Save to Firestore to get survey ID
                const eventsRef = collection(db_resto_new, `_events`);
                const docRef = await addDoc(eventsRef, surveyEvent);
                const surveyId = docRef.id;
                
                console.log('Survey created with ID:', surveyId);
                
                // Upload all images using the survey ID
                const uploadPromises = this.selectedFiles.map((fileObj, index) => 
                    this.uploadSingleImage(fileObj, index, surveyId)
                );
                
                const uploadedUrls = await Promise.all(uploadPromises);
                
                // Update survey document with image URLs and status, initialize with datqa for photogrammetry queue
                const auth = getAuth();
                const user = auth.currentUser;
                const gcsPath = `gs://cg-reef-manager.firebasestorage.app/coral-gardeners/${this.branchId}/surveys/${surveyId}/images/`;
                await updateDoc(docRef, {
                    eventData: {
                        status: 'uploaded',
                        imageUrls: uploadedUrls,
                        exportTypes: ['model', 'pointCloud', 'cameras'], // Array with the three required types
                        gcsPath: gcsPath, // Path to bucket with images
                        id: surveyId, // Same survey ID from Survey collection
                        userEmail: user.email // Email of user who triggered it
                    }
                });
                
                // Show success toast
                EventBus.$emit('show-toast', {
                    message: `Survey uploaded successfully! ${this.totalUploadCount} images processed.`,
                    type: 'success',
                    duration: 5000
                });
                
                // Navigate back
                this.$router.go(-1);
                
            } catch (error) {
                console.error('Error uploading survey:', error);
                EventBus.$emit('show-toast', {
                    message: 'Error uploading survey. Please try again.',
                    type: 'error',
                    duration: 5000
                });
            } finally {
                this.isUploading = false;
            }
        },
        
        async uploadSingleImage(fileObj, index, surveyId) {
            return new Promise((resolve, reject) => {
                const storage = getStorage();
                
                // Create the new simplified path structure
                const fileName = `${index}.jpg`; // Use index as filename (0.jpg, 1.jpg, etc.)
                const imagePath = `coral-gardeners/${this.branchId}/surveys/${surveyId}/images/${fileName}`;
                
                const imageRef = storageRef(storage, imagePath);
                const uploadTask = uploadBytesResumable(imageRef, fileObj.file);
                
                uploadTask.on(
                    'state_changed',
                    (snapshot) => {
                        // Update individual file progress (optional - we could track this per file)
                        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                        fileObj.uploadProgress = progress;
                        
                        // Update overall progress
                        this.updateOverallProgress();
                    },
                    (error) => {
                        console.error(`Error uploading image ${index}:`, error);
                        reject(error);
                    },
                    async () => {
                        try {
                            // Upload completed
                            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                            this.uploadedCount++;
                            
                            // Update progress toast
                            EventBus.$emit('show-toast', {
                                message: `Uploaded ${this.uploadedCount}/${this.totalUploadCount} images (${Math.round(this.uploadProgress)}%)`,
                                type: 'primary',
                                loading: true
                            });
                            
                            resolve(downloadURL);
                        } catch (error) {
                            reject(error);
                        }
                    }
                );
            });
        },
        
        updateOverallProgress() {
            // Calculate overall progress based on uploaded count and individual progress
            const completedFiles = this.uploadedCount;
            const currentFileProgress = this.selectedFiles.reduce((sum, file) => {
                return sum + (file.uploadProgress || 0);
            }, 0) / this.selectedFiles.length / 100;
            
            this.uploadProgress = ((completedFiles + currentFileProgress) / this.totalUploadCount) * 100;
        },
        
        async addToPhotogrammetryQueue(surveyId, surveyDate) {
            try {
                const auth = getAuth();
                const user = auth.currentUser;
                
                if (!user) {
                    console.error('No authenticated user found');
                    return;
                }
                
                // Create GCS path to the images folder (using new simplified structure)
                const gcsPath = `gs://cg-reef-manager.firebasestorage.app/coral-gardeners/${this.branchId}/surveys/${surveyId}/images/`;
                
                // Prepare photogrammetry queue document
                const queueData = {
                    date: surveyDate, // Firestore Date object
                    exportTypes: ['model', 'pointCloud', 'cameras'], // Array with the three required types
                    gcsPath: gcsPath, // Path to bucket with images
                    id: surveyId, // Same survey ID from Survey collection
                    userEmail: user.email // Email of user who triggered it
                };
                
                // Write to top-level PhotogrammetryQueue collection
                const queueRef = collection(db_resto, 'PhotogrammetryQueue');
                const queueDocRef = await addDoc(queueRef, queueData);
                
                console.log('PhotogrammetryQueue entry created with ID:', queueDocRef.id);
                console.log('Queue data:', queueData);
                
            } catch (error) {
                console.error('Error adding to PhotogrammetryQueue:', error);
                // Don't throw error here - we don't want to fail the whole upload if queue write fails
                EventBus.$emit('show-toast', {
                    message: 'Survey uploaded successfully, but photogrammetry processing may be delayed.',
                    type: 'warning',
                    duration: 5000
                });
            }
        },
        
        cancel() {
            this.$router.go(-1);
        }
    },
    watch: {
        currentPage() {
            this.generatePreviewsForCurrentPage();
        }
    }
}
</script> 